import { isCancel, log, password, select, spinner, text } from "@clack/prompts";
import type { AuthMethod, Profile } from "./types";

/**
 * Interactive prompts (TTY only) built on `@clack/prompts`.
 *
 * On Ctrl-C / cancel, clack returns a cancel symbol; we throw
 * `PromptCancelledError` so the command runner can convert it into a clean
 * error result with exit code 130.
 */

export class PromptCancelledError extends Error {
	constructor() {
		super("Prompt cancelled");
		this.name = "PromptCancelledError";
	}
}

function ensure<T>(value: T | symbol): T {
	if (isCancel(value)) throw new PromptCancelledError();
	return value;
}

export async function chooseAuthMethod(): Promise<AuthMethod> {
	const method = await select<AuthMethod>({
		message: "How do you want to authenticate?",
		options: [
			{
				value: "oauth",
				label: "Log in with Whop",
				hint: "Opens your browser to authorize",
			},
			{
				value: "api_key",
				label: "API key",
				hint: "Paste a Whop account API key",
			},
		],
		initialValue: "oauth",
	});
	return ensure(method);
}

export async function promptApiKey(): Promise<string> {
	const key = await password({
		message: "Paste your Whop API key:",
		validate: (value) =>
			value && value.trim().length > 0 ? undefined : "An API key is required",
	});
	return ensure(key).trim();
}

export interface AccountChoice {
	id: string;
	title: string;
	route: string;
}

function accountOptions(accounts: AccountChoice[], currentId?: string) {
	return accounts.map((account) => ({
		value: account.id,
		label: account.title,
		hint: account.id === currentId ? `${account.id} (current)` : account.id,
	}));
}

function findSelectedAccount(
	accounts: AccountChoice[],
	selectedId: string,
): AccountChoice {
	const account = accounts.find((candidate) => candidate.id === selectedId);
	if (!account) {
		throw new Error(
			`Selected business account "${selectedId}" is no longer available.`,
		);
	}
	return account;
}

export async function selectAccount(
	accounts: AccountChoice[],
	currentId?: string,
): Promise<AccountChoice> {
	const id = ensure(
		await select<string>({
			message: "Which business account do you want to use?",
			options: accountOptions(accounts, currentId),
			initialValue: currentId,
		}),
	);
	return findSelectedAccount(accounts, id);
}

export type AccountOrCreateChoice =
	| { kind: "account"; account: AccountChoice }
	| { kind: "create" };

export async function selectAccountOrCreate(
	accounts: AccountChoice[],
	currentId?: string,
): Promise<AccountOrCreateChoice> {
	const createValue = "__create_business__";
	const selected = ensure(
		await select<string>({
			message: "Which business account do you want to use?",
			options: [
				...accountOptions(accounts, currentId),
				{ value: createValue, label: "Create a new business" },
			],
			initialValue: currentId ?? accounts[0]?.id ?? createValue,
		}),
	);
	if (selected === createValue) return { kind: "create" };
	return { kind: "account", account: findSelectedAccount(accounts, selected) };
}

export async function promptBusinessName(): Promise<string> {
	return (
		ensure(
			await text({
				message: "What's your business called?",
				placeholder: "My Business",
				validate: (value) =>
					(value?.trim().length ?? 0) > 0
						? undefined
						: "A business name is required",
			}),
		) ?? ""
	).trim();
}

export async function selectProfile(profiles: Profile[]): Promise<string> {
	const name = await select<string>({
		message: "Select a profile to switch to:",
		options: profiles.map((profile) => ({
			value: profile.name,
			label: profile.name,
			hint:
				profile.accountTitle ??
				profile.userEmail ??
				profile.accountId ??
				profile.userId,
		})),
	});
	return ensure(name);
}

/** Starts a clack spinner and returns it so the caller can `.stop(message)`. */
export function startSpinner(message: string): ReturnType<typeof spinner> {
	const s = spinner();
	s.start(message);
	return s;
}

/** Prints an informational line (e.g. a manual fallback URL or hint). */
export function printInfo(message: string): void {
	log.info(message);
}
